// Spec 078 — rendu de la progression par jalons (US1) + steps & warnings & durée (US4).
// MVP défensif : rendu au fil de l'eau (pas de squelette canonique des 8 jalons) ; libellé de repli
// si label absent. Charte Pragmelis (pas de dégradé, pas de box-shadow décoratif).

import type { AuditJalon, AuditWarning, MilestoneStatus } from '../../types/audit'
import { CANONICAL_JALONS } from '../../lib/auditReference'

type RenderStatus = MilestoneStatus | 'pending'

const STATUS_COLOR: Record<RenderStatus, string> = {
  completed: 'hsl(var(--ok))',
  started: 'hsl(var(--info-text))',
  failed: 'hsl(var(--destructive))',
  skipped: 'hsl(var(--foreground))',
  pending: 'hsl(var(--border))',
}

const STATUS_LABEL: Record<RenderStatus, string> = {
  completed: 'Terminé',
  started: 'En cours',
  failed: 'Échec',
  skipped: 'Ignoré',
  pending: 'À venir',
}

function fmtDuration(ms?: number): string | null {
  if (ms === undefined || ms < 0) return null
  if (ms < 1000) return `${ms} ms`
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s} s`
  const m = Math.floor(s / 60)
  const rs = s % 60
  return `${m} min ${rs.toString().padStart(2, '0')} s`
}

// Spec 082 — les réserves (warnings) ne sont plus rendues dans la timeline : elles sont affichées
// une seule fois, dédupliquées, dans le bloc « réserves » en tête du volet (AuditProgressSheet).
// La timeline reste focalisée sur la progression (jalon · statut · durée · steps).

interface RenderJalon {
  seq: number
  label: string
  status: RenderStatus
  durationMs?: number
  warnings: AuditWarning[]
  steps: AuditJalon['steps']
}

// Squelette des 8 jalons canoniques (contrat §6.1) fusionné avec les jalons émis (au fil de l'eau).
// MVP défensif : les jalons émis hors annexe (seq inattendu) sont quand même rendus, en fin de liste.
function mergeSkeleton(jalons: AuditJalon[]): RenderJalon[] {
  const bySeq = new Map<number, AuditJalon>()
  for (const j of jalons) bySeq.set(j.seq, j)

  const rows: RenderJalon[] = CANONICAL_JALONS.map(c => {
    const e = bySeq.get(c.seq)
    return e
      ? { seq: c.seq, label: e.label || c.label, status: e.status as RenderStatus,
          ...(e.durationMs !== undefined ? { durationMs: e.durationMs } : {}),
          warnings: e.warnings, steps: e.steps }
      : { seq: c.seq, label: c.label, status: 'pending' as RenderStatus, warnings: [], steps: [] }
  })

  // Défensif : jalons émis avec un seq hors 1..8 → ajoutés à la fin.
  for (const j of jalons) {
    if (!CANONICAL_JALONS.some(c => c.seq === j.seq)) {
      rows.push({ seq: j.seq, label: j.label || j.step || 'Jalon', status: j.status as RenderStatus,
        ...(j.durationMs !== undefined ? { durationMs: j.durationMs } : {}),
        warnings: j.warnings, steps: j.steps })
    }
  }
  return rows
}

interface Props {
  jalons: AuditJalon[]
  currentSeq: number | null
  total: number | null
}

export function AuditProgressTimeline({ jalons, currentSeq, total }: Props) {
  const rows = mergeSkeleton(jalons)
  const totalLabel = total ?? rows.length

  return (
    <ol className="space-y-3">
      {rows.map(j => {
        const isCurrent = j.seq === currentSeq && j.status === 'started'
        const color = STATUS_COLOR[j.status]
        const duration = fmtDuration(j.durationMs)
        const isPending = j.status === 'pending'
        return (
          <li
            key={j.seq}
            className="rounded-[3px] border px-3 py-2"
            style={{
              borderColor: isCurrent ? 'hsl(var(--accent))' : 'hsl(var(--border))',
              background: isCurrent ? 'hsl(var(--highlight))' : 'transparent',
              opacity: isPending ? 0.55 : 1,
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: color }}
                  aria-hidden
                />
                <span className="text-body font-medium text-primary">
                  {j.seq}/{totalLabel} — {j.label}
                </span>
              </div>
              <span className="text-caption font-semibold" style={{ color }}>
                {STATUS_LABEL[j.status]}
              </span>
            </div>

            {duration && <p className="mt-0.5 text-caption text-muted-foreground">Durée : {duration}</p>}

            {j.steps.length > 0 && (
              <ul className="mt-1.5 space-y-0.5 border-l pl-3" style={{ borderColor: 'hsl(var(--border))' }}>
                {j.steps.map((s, i) => (
                  <li key={`${s.step}-${i}`} className="text-caption text-muted-foreground">
                    <span style={{ color: STATUS_COLOR[s.status] }}>•</span>{' '}
                    {s.label || s.step}
                  </li>
                ))}
              </ul>
            )}
          </li>
        )
      })}
    </ol>
  )
}
